import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { diagnosticsApi, type AnswerableDiagnostic } from '../api/diagnostics';

/**
 * Learner diagnostics (Phase 1), as a modal launched from the user menu.
 * Lists what's assigned to you and lets you answer. Grading material is
 * stripped server-side, so nothing here can leak the key. Reuses the
 * History modal's scrim styling for consistency.
 */
export function DiagnosticsModal({ onClose }: { onClose: () => void }) {
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="history-modal-scrim" onClick={onClose} role="dialog" aria-modal="true" aria-label="Your diagnostics">
      <div className="history-modal" onClick={(e) => e.stopPropagation()}>
        <header className="history-modal-header">
          <h2 className="history-modal-title">
            {openId ? 'Diagnostic' : 'Your diagnostics'}
          </h2>
          <button type="button" className="history-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>
        <div className="history-modal-body">
          {openId
            ? <Answer id={openId} onBack={() => setOpenId(null)} />
            : <List onOpen={setOpenId} />}
        </div>
      </div>
    </div>
  );
}

function rowCard(): React.CSSProperties {
  return {
    background: 'var(--surface)', border: '1px solid var(--border)',
    borderRadius: 10, padding: 14, marginBottom: 10,
  };
}

function List({ onOpen }: { onOpen: (id: string) => void }) {
  const q = useQuery({ queryKey: ['my-diagnostics'], queryFn: () => diagnosticsApi.mine(), retry: false });
  if (q.isLoading) return <p className="muted">Loading…</p>;
  if (q.isError) return <div style={rowCard()}>Diagnostics aren&apos;t available right now.</div>;
  const rows = q.data?.diagnostics ?? [];
  const pending = rows.filter((r) => r.status === 'pending');
  const done = rows.filter((r) => r.status === 'completed');
  if (rows.length === 0) {
    return <div style={rowCard()}><span className="muted">Nothing assigned to you right now.</span></div>;
  }
  return (
    <div>
      {pending.map((r) => (
        <button key={r.id} onClick={() => onOpen(r.id)} style={{
          ...rowCard(), width: '100%', textAlign: 'left', cursor: 'pointer',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          color: 'inherit', font: 'inherit',
        }}>
          <span>
            <strong>{r.title}</strong>
            <span className="muted" style={{ marginLeft: 8, fontSize: 12 }}>
              {r.question_count} question{r.question_count === 1 ? '' : 's'}
            </span>
          </span>
          <span style={{ color: 'var(--accent)', fontSize: 13 }}>Start →</span>
        </button>
      ))}
      {done.length > 0 && (
        <>
          <h3 style={{ fontSize: 13, color: 'var(--text-dim)', margin: '18px 0 8px' }}>Completed</h3>
          {done.map((r) => (
            <div key={r.id} style={{ ...rowCard(), display: 'flex', justifyContent: 'space-between' }}>
              <span>{r.title}</span>
              <span className="muted" style={{ fontSize: 12 }}>
                {r.submitted_at ? `done ${new Date(r.submitted_at).toLocaleDateString()}` : 'done'}
              </span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function Answer({ id, onBack }: { id: string; onBack: () => void }) {
  const queryClient = useQueryClient();
  const q = useQuery({ queryKey: ['my-diagnostic', id], queryFn: () => diagnosticsApi.get(id), retry: false });
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: (d: AnswerableDiagnostic) =>
      diagnosticsApi.submit(id, d.questions.map((qq) => ({ question_id: qq.id, text: answers[qq.id] || '' }))),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-diagnostics'] });
      onBack();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Submit failed'),
  });

  if (q.isLoading) return <p className="muted">Loading…</p>;
  if (q.isError || !q.data) {
    return <div style={rowCard()}>Couldn&apos;t load this. <button onClick={onBack} style={ghostBtn()}>Back</button></div>;
  }
  const d = q.data;
  if (d.submitted) {
    return (
      <div style={rowCard()}>
        <p>You&apos;ve already completed <strong>{d.title}</strong>.</p>
        <button onClick={onBack} style={ghostBtn()}>← Back to list</button>
      </div>
    );
  }
  const allAnswered = d.questions.every((qq) => (answers[qq.id] || '').trim());
  return (
    <div>
      <button onClick={onBack} style={{ ...ghostBtn(), marginBottom: 10 }}>← Back</button>
      <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
        Answer in your own words. Be honest; it helps find the real gaps.
      </p>
      {d.questions.map((qq, i) => (
        <div key={qq.id} style={rowCard()}>
          <div style={{ marginBottom: 8 }}>
            <span className="muted" style={{ fontSize: 12 }}>Question {i + 1}</span>
            <div style={{ marginTop: 2 }}>{qq.prompt}</div>
          </div>
          <textarea
            value={answers[qq.id] || ''}
            onChange={(e) => setAnswers((p) => ({ ...p, [qq.id]: e.target.value }))}
            rows={3} placeholder="Your answer…"
            style={{
              width: '100%', background: 'var(--bg)', color: 'inherit',
              border: '1px solid var(--border)', borderRadius: 8, padding: 10,
              font: 'inherit', resize: 'vertical',
            }}
          />
        </div>
      ))}
      {error && <div style={{ ...rowCard(), color: 'var(--red)' }}>{error}</div>}
      <button onClick={() => submit.mutate(d)} disabled={!allAnswered || submit.isPending}
        style={primaryBtn(!allAnswered || submit.isPending)}>
        {submit.isPending ? 'Submitting…' : 'Submit answers'}
      </button>
      {!allAnswered && (
        <span className="muted" style={{ marginLeft: 10, fontSize: 12 }}>Answer every question to submit.</span>
      )}
    </div>
  );
}

function ghostBtn(): React.CSSProperties {
  return { background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', font: 'inherit' };
}
function primaryBtn(disabled: boolean): React.CSSProperties {
  return {
    background: disabled ? 'var(--border)' : 'var(--accent)',
    color: disabled ? 'var(--text-dim)' : 'var(--bg)',
    border: 'none', borderRadius: 8, padding: '10px 16px',
    cursor: disabled ? 'default' : 'pointer', fontWeight: 600, font: 'inherit',
  };
}
